import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import API from '../api';
import DateInput from './DateInput';

const GroupScheduling = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    const [doctors, setDoctors] = useState([]);
    const [services, setServices] = useState([]);
    const [patients, setPatients] = useState([]);

    // Termin sa kojim je stranica otvorena (iz Sveske/Kalendara, "Izmeni u grupnim terminima") -
    // ostaje fiksan i identifikuje POSTOJEĆI termin, nezavisno od toga šta korisnik dalje bira.
    const originalDoctorId = params.get('doctorId') || '';
    const originalServiceId = params.get('serviceId') || '';
    const originalDate = params.get('date') || '';
    const originalStartTime = params.get('startTime') || '';
    const isEditMode = Boolean(originalDoctorId);
    const preselectedPatientId = params.get('patientId') || '';

    const [doctorId, setDoctorId] = useState(originalDoctorId);
    const [serviceId, setServiceId] = useState(originalServiceId);
    const [date, setDate] = useState(originalDate || new Date().toISOString().split('T')[0]);
    const [startTime, setStartTime] = useState(originalStartTime);

    const [group, setGroup] = useState([]);
    const [originalGroupMembers, setOriginalGroupMembers] = useState([]);
    const [moving, setMoving] = useState(false);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [patientQuery, setPatientQuery] = useState('');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [adding, setAdding] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const [showNewPatientForm, setShowNewPatientForm] = useState(false);
    const [newPatient, setNewPatient] = useState({ firstName: '', lastName: '', phoneNumber: '', birthDate: '', gender: '' });

    // Doktori podešeni da rade na izabrani datum (WorkingHoursPanel) - null znači "nije podešeno",
    // pa se dozvoljavaju svi doktori. Ne dira originalDoctorId u edit modu.
    const [dayDoctorIds, setDayDoctorIds] = useState(null);

    useEffect(() => {
        const fetchBaseData = async () => {
            try {
                const [resDoctors, resServices, resPatients] = await Promise.all([
                    API.get('/doctors'),
                    API.get('/medical-services'),
                    API.get('/patients')
                ]);
                setDoctors(resDoctors.data);
                setServices(resServices.data);
                setPatients(resPatients.data);
            } catch (err) {
                console.error("Greška pri učitavanju podataka", err);
            }
        };
        fetchBaseData();

        if (isEditMode) {
            API.get('/appointments/group', {
                params: { doctorId: originalDoctorId, date: originalDate, startTime: originalStartTime }
            })
                .then(res => setOriginalGroupMembers(res.data))
                .catch(err => console.error("Greška pri učitavanju originalnog termina", err));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Za izabrani datum proveri da li je podešeno ko od doktora tog dana radi (WorkingHoursPanel).
    useEffect(() => {
        if (!date) { setDayDoctorIds(null); return; }
        API.get('/working-hours', { params: { startDate: date, endDate: date } })
            .then(res => {
                const entry = res.data[0];
                setDayDoctorIds(entry && entry.doctorIds && entry.doctorIds.length > 0 ? entry.doctorIds : null);
            })
            .catch(() => setDayDoctorIds(null));
    }, [date]);

    const availableDoctors = dayDoctorIds
        ? doctors.filter(d => dayDoctorIds.some(id => String(id) === String(d.id)))
        : doctors;

    // Ako trenutno izabrani doktor više nije među dozvoljenima za novi datum, poništi izbor -
    // ali ne i u edit modu, gde originalni doktor mora ostati vidljiv/dostupan.
    useEffect(() => {
        if (isEditMode) return;
        if (dayDoctorIds && doctorId && !dayDoctorIds.some(id => String(id) === String(doctorId))) {
            setDoctorId('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dayDoctorIds]);

    const slotReady = doctorId && serviceId && date && startTime;

    // Da li je korisnik trenutno gleda DRUGI termin od onog sa kojim je stranica otvorena.
    const isViewingDifferentSlot = isEditMode && slotReady && (
        String(doctorId) !== String(originalDoctorId) ||
        String(date) !== String(originalDate) ||
        String(startTime) !== String(originalStartTime)
    );

    // Termini (dugmad) za izabranog doktora/uslugu/datum - uključuje i termine gde već
    // postoji grupa te usluge, da bi se moglo nastaviti dodavanje ljudi u nju.
    useEffect(() => {
        const fetchSlots = async () => {
            if (doctorId && serviceId && date) {
                try {
                    const res = await API.get('/appointments/group-slots', { params: { doctorId, date, serviceId } });
                    setAvailableSlots(res.data);
                } catch (err) {
                    console.error("Greška pri dohvatanju termina", err);
                    setAvailableSlots([]);
                }
            } else {
                setAvailableSlots([]);
            }
        };
        fetchSlots();
    }, [doctorId, serviceId, date]);

    useEffect(() => {
        if (slotReady) {
            fetchGroup();
        } else {
            setGroup([]);
        }
        setLastSaved(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doctorId, serviceId, date, startTime]);

    const fetchGroup = async () => {
        try {
            const res = await API.get('/appointments/group', {
                params: { doctorId, date, startTime }
            });
            setGroup(res.data);
        } catch (err) {
            console.error("Greška pri učitavanju grupe", err);
        }
    };

    const formatPatient = (p) => `${p.firstName} ${p.lastName} — ${p.phoneNumber}`;

    const patientSuggestions = patientQuery.trim().length > 0
        ? patients.filter(p => {
            const q = patientQuery.toLowerCase();
            const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
            const alreadyInGroup = group.some(g => g.patientPhone === p.phoneNumber);
            return !alreadyInGroup && (fullName.includes(q) || (p.phoneNumber || '').includes(patientQuery));
        }).slice(0, 8)
        : [];

    const handlePatientQueryChange = (e) => {
        setPatientQuery(e.target.value);
        setShowSuggestions(true);
        setSelectedPatient(null);
    };

    const pickPatient = (patient) => {
        setSelectedPatient(patient);
        setPatientQuery(formatPatient(patient));
        setShowSuggestions(false);
    };

    // Ako je stranica otvorena klikom na pacijenta (npr. iz liste pacijenata), unapred ga izaberi.
    useEffect(() => {
        if (preselectedPatientId && !selectedPatient && patients.length > 0) {
            const found = patients.find(p => String(p.id) === String(preselectedPatientId));
            if (found) {
                pickPatient(found);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patients]);

    const formatDate = (isoDate) => {
        const [y, m, d] = isoDate.split('-');
        return `${d}.${m}.${y}.`;
    };

    const saveAppointment = async () => {
        if (!selectedPatient) return;
        setAdding(true);
        setLastSaved(null);
        try {
            await API.post('/appointments', {
                patientId: selectedPatient.id,
                doctorId,
                serviceId,
                date,
                startTime,
                note: ''
            });
            const savedName = formatPatient(selectedPatient);
            setPatientQuery('');
            setSelectedPatient(null);
            setShowSuggestions(false);
            await fetchGroup();
            setLastSaved(`Sačuvano: ${savedName} — ${formatDate(date)} u ${startTime}h`);
        } catch (err) {
            alert(err.response?.data?.message || "Greška pri čuvanju pregleda.");
        } finally {
            setAdding(false);
        }
    };

    const startNewPatient = () => {
        const parts = patientQuery.trim().split(/\s+/);
        setNewPatient({
            firstName: parts[0] || '',
            lastName: parts.slice(1).join(' ') || '',
            phoneNumber: '',
            birthDate: '',
            gender: ''
        });
        setShowNewPatientForm(true);
        setShowSuggestions(false);
    };

    const handleNewPatientChange = (e) => {
        setNewPatient({ ...newPatient, [e.target.name]: e.target.value });
    };

    const createPatientAndSchedule = async () => {
        if (!newPatient.firstName || !newPatient.lastName || !newPatient.phoneNumber) return;
        setAdding(true);
        setLastSaved(null);
        try {
            const resPatient = await API.post('/patients', {
                firstName: newPatient.firstName,
                lastName: newPatient.lastName,
                phoneNumber: newPatient.phoneNumber,
                birthDate: newPatient.birthDate || null,
                gender: newPatient.gender || null
            });
            const createdPatient = resPatient.data;
            setPatients(prev => [...prev, createdPatient]);

            await API.post('/appointments', {
                patientId: createdPatient.id,
                doctorId,
                serviceId,
                date,
                startTime,
                note: ''
            });

            const savedName = formatPatient(createdPatient);
            setNewPatient({ firstName: '', lastName: '', phoneNumber: '', birthDate: '', gender: '' });
            setShowNewPatientForm(false);
            setPatientQuery('');
            setSelectedPatient(null);
            await fetchGroup();
            setLastSaved(`Sačuvano (novi pacijent): ${savedName} — ${formatDate(date)} u ${startTime}h`);
        } catch (err) {
            alert(err.response?.data?.message || "Greška pri dodavanju pacijenta ili čuvanju pregleda.");
        } finally {
            setAdding(false);
        }
    };

    const removeFromGroup = async (appointmentId) => {
        if (!window.confirm("Ukloniti ovog pacijenta iz termina?")) return;
        try {
            await API.put(`/appointments/${appointmentId}/cancel`);
            fetchGroup();
        } catch (err) {
            alert("Greška pri uklanjanju pacijenta.");
        }
    };

    // Premešta SVE pacijente sa originalnog termina (sa kojim je stranica otvorena) na
    // trenutno izabrani termin - originalni termin se time oslobađa.
    const moveOriginalGroupHere = async () => {
        if (originalGroupMembers.length === 0) return;
        if (!window.confirm(`Premestiti ${originalGroupMembers.length} ${originalGroupMembers.length === 1 ? 'pacijenta' : 'pacijenata'} sa ${formatDate(originalDate)} u ${originalStartTime}h ovde?`)) {
            return;
        }
        setMoving(true);
        try {
            await Promise.all(originalGroupMembers.map(a =>
                API.put(`/appointments/${a.id}/reschedule`, { date, startTime, doctorId })
            ));
            setOriginalGroupMembers([]);
            await fetchGroup();
        } catch (err) {
            alert(err.response?.data?.message || "Greška pri premeštanju termina. Neki pacijenti su možda već premešteni.");
        } finally {
            setMoving(false);
        }
    };

    return (
        <div style={styles.container}>
            <h2 style={styles.title}>Zakaži grupni termin</h2>
            <p style={styles.hint}>
                Za grupne termine (prvi pregledi, kontrole...) — izaberite doktora, uslugu, datum
                i satnicu, pa dodajte pacijente koji dolaze u toj grupi — svi će biti zakazani na isti termin.
                Datum je unapred popunjen na današnji dan, obavezno proverite pre čuvanja ako zakazujete za neki drugi dan.
            </p>

            {isEditMode && (
                <div style={styles.editBanner}>
                    Otvoreno na izmenu postojećeg termina — dodajte ili uklonite pacijenta ispod,
                    ili izaberite drugi termin da premestite ceo termin tamo.
                </div>
            )}

            <div style={styles.slotForm}>
                <div>
                    <label style={styles.label}>Doktor</label>
                    <select style={styles.input} value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
                        <option value="">Izaberi doktora...</option>
                        {availableDoctors.map(d => <option key={d.id} value={d.id}>{d.firstName} {d.lastName}</option>)}
                    </select>
                    {dayDoctorIds && (
                        <div style={styles.doctorDayHint}>Za ovaj datum radi samo označeni doktor(i).</div>
                    )}
                </div>

                <div>
                    <label style={styles.label}>Usluga</label>
                    <select style={styles.input} value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
                        <option value="">Izaberi uslugu...</option>
                        {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.durationMinutes} min)</option>)}
                    </select>
                </div>

                <div>
                    <label style={styles.label}>Datum</label>
                    <DateInput style={styles.input} value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
            </div>

            {doctorId && serviceId && date && (
                <>
                    <label style={styles.label}>Satnica (početak)</label>
                    <div style={styles.slotGrid}>
                        {startTime && !availableSlots.some(s => s.time.startsWith(startTime)) && (
                            <button style={{ ...styles.slotBtn, ...styles.slotBtnActive }}>
                                {startTime}
                            </button>
                        )}
                        {availableSlots.length === 0 && !startTime && (
                            <div style={{ padding: '10px', color: '#94a3b8', fontSize: '13px' }}>
                                Nema slobodnih termina za izabrani datum.
                            </div>
                        )}
                        {availableSlots.map(slot => {
                            const slotShort = slot.time.substring(0, 5);
                            const isSelected = startTime === slotShort;
                            const selectedServiceName = services.find(s => String(s.id) === String(serviceId))?.name;
                            const isOccupiedByOther = slot.occupiedByService && slot.occupiedByService !== selectedServiceName;
                            const isOccupiedBySame = slot.occupiedByService && slot.occupiedByService === selectedServiceName;

                            let slotStyle = styles.slotBtn;
                            if (isSelected) slotStyle = { ...slotStyle, ...styles.slotBtnActive };
                            else if (isOccupiedByOther) slotStyle = { ...slotStyle, ...styles.slotBtnOccupied };
                            else if (isOccupiedBySame) slotStyle = { ...slotStyle, ...styles.slotBtnGroup };

                            return (
                                <button
                                    key={slot.time}
                                    onClick={() => setStartTime(slotShort)}
                                    style={slotStyle}
                                    title={isOccupiedByOther ? `Već zauzeto: ${slot.occupiedByService} (${slot.occupiedCount})` : (isOccupiedBySame ? `Postojeća grupa: ${slot.occupiedByService} (${slot.occupiedCount})` : undefined)}
                                >
                                    <div>{slotShort}</div>
                                    {slot.occupiedByService && (
                                        <div style={styles.slotSubLabel}>{slot.occupiedByService} ({slot.occupiedCount})</div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}

            {isViewingDifferentSlot && originalGroupMembers.length > 0 && (
                <div style={styles.moveBanner}>
                    <span>
                        Premestiti ceo termin sa {formatDate(originalDate)} u {originalStartTime}h
                        ({originalGroupMembers.length} {originalGroupMembers.length === 1 ? 'pacijent' : 'pacijenata'}) ovde?
                    </span>
                    <button style={styles.moveBtn} onClick={moveOriginalGroupHere} disabled={moving}>
                        {moving ? 'Premeštanje...' : 'Premesti ovde'}
                    </button>
                </div>
            )}

            {slotReady && (
                <>
                    <div style={styles.groupSection}>
                        <h3 style={styles.groupTitle}>
                            Grupa ({group[0]?.serviceName || services.find(s => String(s.id) === String(serviceId))?.name}) {date} u {startTime} — {group.length} {group.length === 1 ? 'pacijent' : 'pacijenata'}
                        </h3>

                        <div style={styles.groupList}>
                            {group.length === 0 && (
                                <div style={{ padding: '15px', color: '#94a3b8', fontSize: '14px' }}>
                                    Još niko nije dodat u ovaj termin.
                                </div>
                            )}
                            {group.map(a => (
                                <div key={a.id} style={styles.groupRow}>
                                    <div>
                                        <div style={styles.groupName}>{a.patientFullName}</div>
                                        <div style={styles.groupPhone}>{a.patientPhone}</div>
                                    </div>
                                    <button style={styles.removeBtn} onClick={() => removeFromGroup(a.id)}>Ukloni</button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <label style={styles.label}>Dodaj pacijenta u grupu</label>
                    {!showNewPatientForm && (
                        <div style={styles.addRow}>
                            <div style={styles.autocompleteWrapper}>
                                <input
                                    type="text"
                                    style={{
                                        ...styles.input,
                                        border: selectedPatient ? '1px solid #10b981' : styles.input.border
                                    }}
                                    placeholder="Ukucajte ime, prezime ili broj telefona..."
                                    value={patientQuery}
                                    onChange={handlePatientQueryChange}
                                    onFocus={() => setShowSuggestions(true)}
                                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                                    disabled={adding}
                                />
                                {showSuggestions && patientSuggestions.length > 0 && (
                                    <div style={styles.suggestionsList}>
                                        {patientSuggestions.map(p => (
                                            <div key={p.id} style={styles.suggestionItem} onMouseDown={() => pickPatient(p)}>
                                                {formatPatient(p)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {showSuggestions && patientQuery.trim().length > 0 && patientSuggestions.length === 0 && (
                                    <div style={styles.suggestionsList}>
                                        <div style={{ ...styles.suggestionItem, cursor: 'default', color: '#94a3b8' }}>
                                            Nema pacijenata za "{patientQuery}"
                                        </div>
                                        <div style={{ ...styles.suggestionItem, color: '#1c8aa6', fontWeight: '600' }} onMouseDown={startNewPatient}>
                                            + Dodaj kao novog pacijenta
                                        </div>
                                    </div>
                                )}
                            </div>
                            <button
                                style={{ ...styles.saveBtn, opacity: (!selectedPatient || adding) ? 0.6 : 1 }}
                                onClick={saveAppointment}
                                disabled={!selectedPatient || adding}
                            >
                                {adding ? 'Čuvanje...' : 'Sačuvaj pregled'}
                            </button>
                        </div>
                    )}

                    {showNewPatientForm && (
                        <div style={styles.newPatientBox}>
                            <div style={styles.newPatientTitle}>Novi pacijent (biće odmah dodat u bazu i zakazan)</div>
                            <div style={styles.newPatientGrid}>
                                <input name="firstName" placeholder="Ime *" value={newPatient.firstName} onChange={handleNewPatientChange} style={styles.input} disabled={adding} />
                                <input name="lastName" placeholder="Prezime *" value={newPatient.lastName} onChange={handleNewPatientChange} style={styles.input} disabled={adding} />
                                <input name="phoneNumber" placeholder="Telefon *" value={newPatient.phoneNumber} onChange={handleNewPatientChange} style={styles.input} disabled={adding} />
                                <input name="birthDate" type="date" value={newPatient.birthDate} onChange={handleNewPatientChange} style={styles.input} disabled={adding} />
                                <select name="gender" value={newPatient.gender} onChange={handleNewPatientChange} style={styles.input} disabled={adding}>
                                    <option value="">Pol (opciono)</option>
                                    <option value="MALE">Muški</option>
                                    <option value="FEMALE">Ženski</option>
                                </select>
                            </div>
                            <div style={styles.newPatientActions}>
                                <button
                                    style={{ ...styles.saveBtn, opacity: (!newPatient.firstName || !newPatient.lastName || !newPatient.phoneNumber || adding) ? 0.6 : 1 }}
                                    onClick={createPatientAndSchedule}
                                    disabled={!newPatient.firstName || !newPatient.lastName || !newPatient.phoneNumber || adding}
                                >
                                    {adding ? 'Čuvanje...' : 'Sačuvaj pacijenta i zakaži'}
                                </button>
                                <button style={styles.cancelBtn} onClick={() => setShowNewPatientForm(false)} disabled={adding}>
                                    Otkaži
                                </button>
                            </div>
                        </div>
                    )}
                    {lastSaved && <div style={styles.savedBanner}>{lastSaved}</div>}
                </>
            )}
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '700px',
        margin: '0 auto',
        padding: '15px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    title: { margin: '0 0 8px 0', fontSize: '24px', color: '#1f2937' },
    hint: { fontSize: '13px', color: '#7f8c8d', marginBottom: '20px', lineHeight: '1.5' },
    editBanner: { backgroundColor: '#fef3c7', color: '#92400e', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', marginBottom: '15px' },
    moveBanner: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
        backgroundColor: '#eef2ff', color: '#3730a3', padding: '12px 14px', borderRadius: '8px',
        fontSize: '13px', fontWeight: '600', marginBottom: '15px'
    },
    moveBtn: {
        padding: '8px 14px', backgroundColor: '#4338ca', color: 'white', border: 'none',
        borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap'
    },
    slotForm: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '15px',
        backgroundColor: '#f1f3f5',
        padding: '15px',
        borderRadius: '10px',
        marginBottom: '20px'
    },
    label: { display: 'block', marginBottom: '6px', fontWeight: '600', color: '#475569', fontSize: '13px' },
    input: { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' },
    doctorDayHint: { marginTop: '4px', fontSize: '11px', color: '#0d9488', fontWeight: '600' },
    slotGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
        gap: '8px',
        marginBottom: '20px'
    },
    slotBtn: {
        padding: '8px 6px', borderRadius: '8px', cursor: 'pointer', fontWeight: '500',
        backgroundColor: '#f1f5f9', color: '#1e293b', border: '1px solid #cbd5e1', textAlign: 'center'
    },
    slotBtnActive: { backgroundColor: '#10b981', color: '#fff', border: '1px solid #059669' },
    slotBtnGroup: { backgroundColor: '#e4f4f7', color: '#125c70', border: '1px solid #8fd0dc' },
    slotBtnOccupied: { backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' },
    slotSubLabel: { fontSize: '10px', fontWeight: 'normal', marginTop: '2px' },
    groupSection: { marginBottom: '20px' },
    groupTitle: { fontSize: '16px', color: '#1f2937', marginBottom: '10px' },
    groupList: { backgroundColor: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
    groupRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 15px',
        borderBottom: '1px solid #eee'
    },
    groupName: { fontSize: '15px', fontWeight: '600', color: '#1f2937' },
    groupPhone: { fontSize: '13px', color: '#7f8c8d' },
    removeBtn: { padding: '6px 12px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
    addRow: { display: 'flex', gap: '10px', alignItems: 'flex-start' },
    saveBtn: { padding: '10px 18px', backgroundColor: '#1c8aa6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    savedBanner: { marginTop: '12px', padding: '10px 14px', backgroundColor: '#dcfce7', color: '#166534', borderRadius: '8px', fontSize: '13px', fontWeight: '600' },
    newPatientBox: {
        backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px'
    },
    newPatientTitle: { fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '10px' },
    newPatientGrid: {
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px'
    },
    newPatientActions: { display: 'flex', gap: '10px' },
    cancelBtn: {
        padding: '10px 18px', backgroundColor: '#e2e8f0', color: '#334155', border: 'none',
        borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold'
    },
    autocompleteWrapper: { position: 'relative', flex: 1 },
    suggestionsList: {
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: 0,
        right: 0,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        zIndex: 10,
        maxHeight: '220px',
        overflowY: 'auto'
    },
    suggestionItem: {
        padding: '10px 12px',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#1e293b',
        borderBottom: '1px solid #f1f5f9'
    }
};

export default GroupScheduling;
